import { GlassPanel } from '@/components/glass/GlassPanel';
import { cn } from '@/lib/utils';

export interface NearestFiveCTAProps {
  /** Click handler — drops radius + county filters and shows the 5 nearest properties. */
  onShowNearestFive: () => void;
  className?: string;
}

/**
 * `<NearestFiveCTA>` — empty-state CTA when filter result count = 0.
 *
 * Copy locked verbatim by UI-SPEC § 8:
 * - Heading: "No properties match your filters."
 * - Body:    "Show the 5 nearest properties regardless of radius and county."
 * - Button:  "Show nearest 5 properties"
 *
 * The bronze button is one of the 6 places bronze is allowed (UI-SPEC
 * § 1.1.1 #5). aria-label per § 9.3.
 */
export function NearestFiveCTA({ onShowNearestFive, className }: NearestFiveCTAProps) {
  return (
    <GlassPanel
      variant="strong"
      className={cn('p-12 text-center max-w-[1100px]', className)}
    >
      <h2 className="text-xl font-semibold text-foam-50 mb-2">
        No properties match your filters.
      </h2>
      <p className="text-base text-foam-50/70 mb-6">
        Show the 5 nearest properties regardless of radius and county.
      </p>
      <button
        type="button"
        onClick={onShowNearestFive}
        aria-label="Show nearest five properties regardless of radius and county"
        className={cn(
          'bg-bronze-500 text-ocean-950 px-6 h-12 rounded-2xl font-semibold',
          'hover:bg-bronze-500/90 transition-colors duration-150',
          'focus-visible:outline-2 focus-visible:outline-bronze-500/60 focus-visible:outline-offset-2',
        )}
      >
        Show nearest 5 properties
      </button>
    </GlassPanel>
  );
}
